import React, { useEffect, useState } from "react";
import { Flex, Text, SegmentedControl, Progress } from "@radix-ui/themes";
import { useLocationContext, useDateRangeContext } from "context";
import { usePresenceMetrics } from "../../hooks/presence/usePresenceMetrics";
import { MetricCard, ChartCard, DatePickerWithRange, LocationMultiSelect } from "components";
import { isEqual, startOfDay } from "date-fns";
import { presenceChartConfig } from "../../configs/PresenceChartConfig";
import { useGetZones } from "hooks";
import { ZonesMultiSelect } from "components/zonesMultiSelect/ZonesMultiSelect";
import PresenceAnalyticsPageBG from "../../assets/images/PresenceAnalyticsPageBG.png";

export default function Presence() {
  const { initialItems, locationSelection, setLocationSelection, isLocationsSuccess } = useLocationContext();
  const { selectedDateRange, setSelectedDateRange, setActivePage } = useDateRangeContext();

  // Ensure Presence has its own independent date range state
  useEffect(() => {
    setActivePage("presence");
  }, [setActivePage]);

  const [isImageLoaded, setIsImageLoaded] = useState(false);
  useEffect(() => {
    const img = new Image();
    img.src = PresenceAnalyticsPageBG;
    img.onload = () => {
      setIsImageLoaded(true);
    };
  }, []);

  const [zoneSelection, setZoneSelection] = useState([]);
  const [activeView, setActiveView] = useState(Object.keys(presenceChartConfig)[0]);
  const [disableBefore, setDisableBefore] = useState(null);
  
  const { data: zonesData, isLoading: isZonesLoading } = useGetZones(locationSelection?.map((location) => parseInt(location)) || []);
  useEffect(() => {
    if (zonesData && zonesData.length > 0) {
      const allZoneIds = zonesData.map((zone) => zone.id.toString());
      setZoneSelection(allZoneIds);
    }
  }, [zonesData]);
  const zoneOptions = zonesData || [];

  const { presenceMetrics, isLoading: isLoadingPresenceMetrics } = usePresenceMetrics(selectedDateRange, locationSelection, zoneSelection, isLocationsSuccess);

  useEffect(() => {
    if (presenceMetrics?.disable_before_date) {
      setDisableBefore(presenceMetrics.disable_before_date);
    }
  }, [presenceMetrics]);

  const metricTileData = [
    {
      name: "Est. unique guests",
      icon: "",
      metric: presenceMetrics?.est_guest ?? "Loading...",
      updates: isEqual(startOfDay(selectedDateRange.from), startOfDay(presenceMetrics?.disable_before_date))
        ? "The last period does not have data"
        : `${presenceMetrics?.last_period_changes.visitor_change >= 0 ? "+" : ""}${presenceMetrics?.last_period_changes.visitor_change ?? "..."}% since last period`,
      tooltip: "Total estimated guests in the selected locations",
    },
    {
      name: "Est. unique passerby",
      icon: "",
      metric: presenceMetrics?.est_passerby ?? "Loading...",
      updates: isEqual(startOfDay(selectedDateRange.from), startOfDay(presenceMetrics?.disable_before_date))
        ? "The last period does not have data"
        : `${presenceMetrics?.last_period_changes.passerby_change >= 0 ? "+" : ""}${presenceMetrics?.last_period_changes.passerby_change ?? "..."}% since last period`,
      tooltip: "Total estimated passersby near the selected locations",
    },
    {
      name: "Est. passerby conversion",
      icon: "",
      metric: `${presenceMetrics?.passerby_conversion ?? "Loading..."}%`,
      updates: isEqual(startOfDay(selectedDateRange.from), startOfDay(presenceMetrics?.disable_before_date))
        ? "The last period does not have data"
        : `${presenceMetrics?.last_period_changes.conversion_change >= 0 ? "+" : ""}${presenceMetrics?.last_period_changes.conversion_change ?? "..."}% since last period`,
      tooltip: "Percentage of passersby that became guests",
    },
    {
      name: "Avg. guest dwell",
      icon: "",
      metric: `${presenceMetrics?.avg_guest_dwell ?? "Loading..."} mins`,
      updates: isEqual(startOfDay(selectedDateRange.from), startOfDay(presenceMetrics?.disable_before_date))
        ? "The last period does not have data"
        : `${presenceMetrics?.last_period_changes.dwell_change >= 0 ? "+" : ""}${presenceMetrics?.last_period_changes.dwell_change ?? "..."}% since last period`,
      tooltip: "Average time guests spent in the location",
    },
  ];

  const oneDayInMilliseconds = 24 * 60 * 60 * 1000;
  const normalizedFrom = startOfDay(selectedDateRange.from);
  const normalizedTo = startOfDay(selectedDateRange.to);
  const selectedDateRangeInDays = Math.ceil((normalizedTo.getTime() - normalizedFrom.getTime()) / oneDayInMilliseconds) + 1;

  // Determine which charts to show based on date range
  let chartTypesToShow = [];
  if (selectedDateRangeInDays === 1) {
    chartTypesToShow = ["timeOfDay"];
  } else if (selectedDateRangeInDays > 1 && selectedDateRangeInDays < 62) {
    chartTypesToShow = ["day", "timeOfDay", "dayOfWeek"];
  } else if (selectedDateRangeInDays >= 62) {
    chartTypesToShow = ["month", "day", "timeOfDay", "dayOfWeek"];
  }

  const showNoResults = !isLoadingPresenceMetrics && (!selectedDateRange?.from || !locationSelection?.length || !presenceMetrics?.est_guest);

  const renderContent = () => {
    if (showNoResults) {
      return (
        <Flex direction="column" style={{ alignSelf: "stretch", flex: "1 0 0", alignItems: "center", justifyContent: "center",
          height: "100%", width: "100%" }}>
          <Flex p="5" direction="column" gap="5" style={{ alignSelf: "stretch", alignItems: "center",
              justifyContent: "center", width: "100%", borderRadius: "8px", border: "1px dashed var(--gray-a6)",
              flex: 1, position: "relative", backgroundColor: "var(--color-background)",}}>
            <Flex direction="column" gap="2" style={{ alignItems: "center" }}>
              <Text size="5" weight="medium">No results</Text>
              <Text size="2" weight="regular" color="gray" style={{ width: "400px", textAlign: "center" }}>
                Adjust filters or check if you have an active Wi-Fi Presence integration of Cisco Meraki.
              </Text>
            </Flex>
          </Flex>
        </Flex>
      );
    }

    return (
      <Flex direction="column" gap="4" style={{ width: "100%" }}>
        {/* Metrics Cards */}
        <Flex className="card-container" style={{ alignSelf: "stretch" }} align="start" gap="5">
          {metricTileData.map((entry, index) => (
            <MetricCard key={index} metricName={entry.name} metricIcon={entry.icon} metric={entry.metric} updates={entry.updates} tooltip={entry.tooltip} />
          ))}
        </Flex>

        {/* Charts Section */}
        <Flex direction="column" gap="5" style={{ alignSelf: "stretch", alignItems: "center" }}>
          {chartTypesToShow.map((chartType) => {
            const config = presenceChartConfig[activeView][chartType];
            return (
              <ChartCard key={chartType} text={config.text} tooltip={config.tooltip}>
                <config.ChartComponent
                  data={{
                    data: presenceMetrics,
                  }}
                  startTimeMilliseconds={selectedDateRange.from.getTime()}
                  endTimeMilliseconds={selectedDateRange.to.getTime()}
                />
              </ChartCard>
            );
          })}
        </Flex>
      </Flex>
    );
  };

  const isPageLoading = isZonesLoading;
  
  if (isPageLoading) {
    return (
      <div style={{ 
        position: "fixed", 
        top: 0, 
        left: 0, 
        width: "100%", 
        zIndex: 1000 
      }}>
        <Progress size="1" duration="1" />
      </div>
    );
  }

  return (
    <Flex className="content-container" direction="column"
      style={{ alignSelf: "stretch", width: "100%", height: "100%", alignItems: "center", flex: "1 0 0" }}>
      {isLoadingPresenceMetrics && (
        <div style={{ 
          position: "fixed", 
          top: 0, 
          left: 0, 
          width: "100%", 
          zIndex: 1000 
        }}>
          <Progress size="1" duration="1" />
        </div>
      )}
      <Flex direction="column" gap="7" p="5" style={{ alignSelf: "stretch", minHeight: "180px",
        background: isImageLoaded
          ? `linear-gradient(180deg, rgba(17, 17, 19, 0.40) -100%, var(--color-background) 100%), url(${PresenceAnalyticsPageBG}) no-repeat center center / cover`
          : 'var(--gray-a1)',
        backgroundBlendMode: 'luminosity',
      }}>
        {/* Header Title and Background */}
        <Flex direction="column" gap="2" style={{ alignSelf: "stretch", alignContent: "center", justifyContent: "center" }}>
          <Text weight="medium" size="5" align="center">Presence Analytics</Text>
          <Text weight="regular" size="1" color="gray" align="center">Intelligently identify guests and passerby from Wi-Fi devices.</Text>
        </Flex>
        {/* Filters Section */}
        <Flex className="filter-bar" style={{ alignSelf: "stretch" }} justify="between" align="center" gap="2">
          <Flex>
            <SegmentedControl.Root value={activeView} onValueChange={setActiveView}>
              <SegmentedControl.Item value="presence">Presence</SegmentedControl.Item>
              <SegmentedControl.Item value="dwell">Dwell</SegmentedControl.Item>
            </SegmentedControl.Root>
          </Flex>
          <Flex gap="2" align="center" style={{ marginLeft: "auto" }}>
            <DatePickerWithRange value={selectedDateRange} onDateChange={setSelectedDateRange} disableBeforeDate={disableBefore} />
            {zoneOptions.length > 0 && <ZonesMultiSelect options={zoneOptions} defaultValue={zoneSelection} onValueChange={setZoneSelection} />}
            <LocationMultiSelect options={initialItems} defaultValue={locationSelection} onValueChange={setLocationSelection} />
          </Flex>
        </Flex>
      </Flex>
      <Flex direction="column" px="5" pb="5" gap="5" style={{alignSelf: "stretch", flex: "1 0 0", alignItems: "center",
          overflowY: "scroll" }}>
        {renderContent()}
      </Flex>
    </Flex>
  );
}
